'use client';
import base, { AxiosInstance } from 'axios';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { AuthContext, clearStorage } from './Authentication';
import { useRBACOptional } from '@/contexts/RBACContext';
import { fetchSubTenantMemberUserIds } from '@/lib/subTenantMemberUserIds';
import { listAllAccounts } from '@/services/rbacApi';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { apiConfig, getMainApiOrigin, getSelfOriginForRedirects } from '@/config/apiConfig';
import { extractApiMessage } from './Axios';
import {
  getActiveTenantHeaders,
  readActiveTenantScope,
  resolveActiveRequestUserId,
} from '@/lib/activeTenantScope';

export const DigitalAdAxiosContext = createContext<AxiosInstance | null>(null);

/** SWR/cache key segment: changes when sub-tenant scope, member IDs, or account (e.g. reset to owner) change. */
export const DigitalAdSubTenantMemberKeyContext = createContext<string>('');

/**
 * True when member IDs are resolved and safe to fire requests.
 * False during the initial loading frame when a sub-tenant scope is active but
 * fetchSubTenantMemberUserIds hasn't returned yet (prevents sending memberUserIds:[]).
 */
export const DigitalAdMemberIdsReadyContext = createContext<boolean>(true);

/**
 * True once the org-owner's root-account member IDs have been fetched (or confirmed
 * unnecessary for this user). Gates getCampaign so a member-created campaign is
 * resolved before the request fires.
 */
export const DigitalAdOwnerMemberIdsReadyContext = createContext<boolean>(true);

export const isCancel = base.isCancel;

type AuthShape = {
  userId?: string | number | null;
  token?: string | null;
  isAuthenticated?: boolean;
};

function getDigitalAdInstance(
  auth: AuthShape,
  setSessionExpiredOpen: (v: boolean) => void,
  aggregateMemberIdsRef: MutableRefObject<number[] | null>,
  subTenantScopeActiveRef: MutableRefObject<boolean>,
  listingSubAccountIdRef: MutableRefObject<number | null>,
  ownerDefaultMemberIdsRef: MutableRefObject<number[] | null>,
  isOrgOwnerRef: MutableRefObject<boolean>,
  isRootContextActiveRef: MutableRefObject<boolean>,
  ownerAccountIdRef: MutableRefObject<number | null>
) {
  const digitalAdBase = apiConfig.baseUrls.digitalAd.replace(/\/+$/, '');
  const axios = base.create({
    baseURL: digitalAdBase,
    withCredentials: true,
  });

  const auth_exclude: Array<string> = [];

  axios.interceptors.request.use((req) => {
    const tenantScope = readActiveTenantScope();
    const scopedAccountId =
      listingSubAccountIdRef.current ??
      tenantScope.activeAccountId ??
      tenantScope.rootListingAccountId ??
      null;
    const accountAggregateActive =
      subTenantScopeActiveRef.current ||
      scopedAccountId != null ||
      isRootContextActiveRef.current;

    const method = (req.method || 'get').toLowerCase();
    const url = typeof req.url === 'string' ? req.url : '';
    const isAggregateListUrl =
      method === 'post' &&
      (url.includes('digitalAd/campaigns') ||
        url.includes('digitalAd/campaign/analytics/organisation') ||
        url.includes('digitalAd/campaigns/analytics') ||
        url.includes('digitalAd/campaign/analytics/export'));

    // For account-aggregate campaign lists, keep body user_id as the session user (owner)
    // when the owner has switched into a member. The working root listing uses the owner
    // user id + memberUserIds; rewriting to the switched member drops the org-wide set
    // on backends that key NULL-account_id rows off request.user_id.
    const switchedMemberId = tenantScope.activeUserId;
    const sessionUserId = auth.userId ?? null;
    const useSessionUserForAggregateBody =
      isAggregateListUrl &&
      accountAggregateActive &&
      switchedMemberId != null &&
      sessionUserId != null &&
      String(switchedMemberId) !== String(sessionUserId);
    const activeUserId = useSessionUserForAggregateBody
      ? sessionUserId
      : resolveActiveRequestUserId(auth.userId);

    if (!req.data) {
      req.data = {};
    }
    if (!req.data.userID && activeUserId) {
      if (req.data instanceof FormData) {
        req.data.append('userID', String(activeUserId));
        req.data.append('user_id', String(activeUserId));
      } else {
        req.data.userID = activeUserId;
        if (req.data.user_id === undefined) {
          req.data.user_id = activeUserId;
        }
      }
    } else if (
      activeUserId &&
      req.data &&
      typeof req.data === 'object' &&
      !(req.data instanceof FormData) &&
      req.data.user_id === undefined
    ) {
      // FastAPI / Pydantic bodies often expect snake_case `user_id` while legacy payloads use `userID`.
      req.data.user_id = activeUserId;
    }

    // Owner switched to a member: force aggregate list bodies onto the session owner id
    // even if a caller pre-set userID to the switched member.
    if (
      useSessionUserForAggregateBody &&
      req.data &&
      typeof req.data === 'object' &&
      !(req.data instanceof FormData)
    ) {
      req.data.userID = sessionUserId;
      req.data.user_id = sessionUserId;
    }

    if (req.data && typeof req.data === 'object' && !(req.data instanceof FormData)) {
      if (method === 'post') {
        const aggregate =
          url.includes('digitalAd/campaigns') ||
          url.includes('digitalAd/getCampaign') ||
          url.includes('digitalAd/campaigns/autofill') ||
          url.includes('digitalAd/campaign/analytics/organisation') ||
          url.includes('digitalAd/campaign/analytics/export') ||
          url.includes('digitalAd/campaigns/analytics') ||
          url.includes('digitalAd/campaign/duplicate') ||
          url.includes('digitalAd/campaign/activate') ||
          url.includes('digitalAd/campaign/end') ||
          url.includes('digitalAd/delete') ||
          url.includes('digitalAd/campaign/action/bulk');
        if (aggregate && accountAggregateActive) {
          // Root org context / owner member-switch: prefer org-wide member IDs so
          // sub-account campaigns are included. Fall back to listing-account members.
          if (
            (isRootContextActiveRef.current || tenantScope.rootListingAccountId != null) &&
            ownerDefaultMemberIdsRef.current != null
          ) {
            req.data.memberUserIds = ownerDefaultMemberIdsRef.current;
          } else {
            // Ref is null until useEffect runs after sub-tenant switch; use [] so we never
            // fall back to owner-only listing during that frame (backend: explicit empty = no rows).
            req.data.memberUserIds = aggregateMemberIdsRef.current ?? [];
          }
        } else if (url.includes('digitalAd/getCampaign') && !accountAggregateActive && isOrgOwnerRef.current) {
          // Org owner accessing a campaign in their default context (no sub-account scope active):
          // inject root-account member IDs so campaigns created by org members are still found.
          const fallback = ownerDefaultMemberIdsRef.current;
          if (fallback != null) {
            req.data.memberUserIds = fallback;
          }
        }
      }
    }
    if (auth.isAuthenticated && auth_exclude.indexOf(req.url!) == -1) {
      if (!req.data.session_token) {
        req.data.session_token = auth.token;
      }
    }
    // Add cookies header for digital ad API
    req.headers['cookies'] = document.cookie;
    req.headers['X-Frontend-Page'] = window.location.href;
    Object.assign(req.headers, getActiveTenantHeaders());
    // For non-org-owner users, rbac_selected_subtenant_id is null in localStorage so
    // getActiveTenantHeaders() returns no X-Active-Account-Id. Set it from the effective
    // listing scope so the backend filters by account_id (shows all sub-account campaigns),
    // not just by user_id (which only shows that user's own campaigns).
    // Falls back to the owner's own account (ownerAccountIdRef) when no sub-account/root
    // scope is active at all — the plain default context — so this header is still sent
    // instead of being omitted entirely; kept separate from scopedAccountId so it doesn't
    // flip accountAggregateActive/memberUserIds behavior above.
    const headerAccountId = scopedAccountId ?? ownerAccountIdRef.current;
    if (headerAccountId != null && !req.headers['X-Active-Account-Id']) {
      req.headers['X-Active-Account-Id'] = String(headerAccountId);
    }

    return req;
  });

  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (window.navigator.onLine && err.response) {
        if (err.response.status == 401) {
          const mainOrigin = getMainApiOrigin();
          if (
            auth.isAuthenticated &&
            err.response.config.url != `${mainOrigin}/api/logout`
          ) {
            location.href = `${getSelfOriginForRedirects()}/v2/login`;
          } else if (err.response.config.url == `${mainOrigin}/api/userLogin`) {
            location.href = `${getSelfOriginForRedirects()}/account-disabled`;
          } else {
            setSessionExpiredOpen(true);
          }
        } else {
          const status = err.response.status;
          const apiMessage = extractApiMessage(err.response?.data);
          const isProd = process.env.NODE_ENV === 'production';
          err.errorCode = status;
          err.formattedError = (isProd && status >= 500 ? null : apiMessage) || `Request failed with status ${status}`;
        }
      } else {
        err.formattedError =
          'An error occurred while connecting to the network';
      }
      return Promise.reject(err);
    }
  );
  return axios;
}

export default function DigitalAdAxios({ children }: { children: ReactNode }) {
  const authData = useContext(AuthContext);
  const rbac = useRBACOptional();
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [subTenantMemberKey, setSubTenantMemberKey] = useState('');
  const aggregateMemberIdsRef = useRef<number[] | null>(null);
  const subTenantScopeActiveRef = useRef(false);
  const listingSubAccountIdRef = useRef<number | null>(null);

  const tenantScope = readActiveTenantScope();
  const listingSubAccountId =
    rbac?.effectiveListingSubAccountId ??
    (rbac?.isRootContextActive && rbac?.defaultAccountId != null ? rbac.defaultAccountId : null) ??
    rbac?.selectedSubAccountId ??
    tenantScope.activeAccountId ??
    tenantScope.rootListingAccountId ??
    null;
  const selectedMemberExternalUserId = rbac?.selectedSubTenantMemberExternalUserId ?? null;
  // Treat localStorage root/sub scope as active even if RBAC React state hasn't caught up —
  // otherwise X-Active-Account-Id is sent (from headers) without memberUserIds.
  const isSubTenantListingScope =
    Boolean(listingSubAccountId) || Boolean(tenantScope.isAggregateSubTenantScope);
  if (!isSubTenantListingScope) {
    aggregateMemberIdsRef.current = null;
  }
  subTenantScopeActiveRef.current = isSubTenantListingScope;
  listingSubAccountIdRef.current = listingSubAccountId;

  // Owner's own account id, used only as a header fallback when no sub-account/root scope
  // is active at all (the plain default context) — kept out of listingSubAccountId so it
  // doesn't affect accountAggregateActive/memberUserIds behavior above.
  const ownerAccountIdRef = useRef<number | null>(null);
  ownerAccountIdRef.current = rbac?.currentAccountId ?? rbac?.defaultAccountId ?? null;

  // Stable refs to avoid adding owner/defaultAccount to the useEffect dependency array
  const ownerUserIdRef = useRef<number | null>(null);
  ownerUserIdRef.current = rbac?.userContext?.owner_user_id
    ? Number(rbac.userContext.owner_user_id)
    : null;
  const defaultAccountIdRef = useRef<number | null>(null);
  defaultAccountIdRef.current = rbac?.defaultAccountId ?? null;

  // Member IDs for the root account, kept fresh for org owners so getCampaign can
  // resolve member-created campaigns even when no sub-account scope is active.
  const ownerDefaultMemberIdsRef = useRef<number[] | null>(null);
  const isOrgOwnerRef = useRef<boolean>(false);
  isOrgOwnerRef.current = Boolean(rbac?.userContext?.is_org_owner);
  // The plain default/unpicked context (no sub-account, no explicit root-context flag) is an
  // org owner's implicit "Organization" view — RBACContext's own effectiveListingSubAccountId
  // already treats it identically to isRootContextActive (see isDefaultUnpickedContext there).
  // Without mirroring that here, this flag stayed false for that owner, so the interceptor
  // below always fell back to aggregateMemberIdsRef (a single account's member fetch) instead
  // of ownerDefaultMemberIdsRef (the full org-wide sweep) — silently dropping members granted
  // only under a different sub-account (e.g. one included in some requests but not others).
  const isDefaultUnpickedContext =
    !Boolean(rbac?.isRootContextActive) && (rbac?.selectedSubAccountId ?? null) == null;
  const isRootListing =
    Boolean(rbac?.isRootContextActive) ||
    Boolean(tenantScope.rootListingAccountId) ||
    (isDefaultUnpickedContext && isOrgOwnerRef.current);
  const isRootContextActiveRef = useRef<boolean>(false);
  isRootContextActiveRef.current = isRootListing;
  const [ownerMemberIdsReady, setOwnerMemberIdsReady] = useState(false);

  useEffect(() => {
    if (!listingSubAccountId) {
      aggregateMemberIdsRef.current = null;
      setSubTenantMemberKey('');
      return;
    }
    const subId = listingSubAccountId;
    const orgId = rbac?.currentOrgId;
    aggregateMemberIdsRef.current = [];
    setSubTenantMemberKey(`loading:${subId}`);
    let cancelled = false;
    (async () => {
      try {
        const ids = await fetchSubTenantMemberUserIds(subId, {
          orgId,
          bypassCache: true,
        });
        // Include the org owner's campaigns in root-account listings so campaigns created
        // by the owner at org level are visible to org members (BOOSTT-2969).
        const ownerExtId = ownerUserIdRef.current;
        const rootAccId = defaultAccountIdRef.current;
        if (ownerExtId != null && Number.isFinite(ownerExtId) && subId === rootAccId && !ids.includes(ownerExtId)) {
          ids.push(ownerExtId);
        }
        // Sub-tenants are created FOR the member (the member IS the sub-tenant), so they
        // have no access-grant rows and fetchSubTenantMemberUserIds returns [].
        // Fall back to the member's own external user ID so the backend scopes to their data.
        const effectiveIds =
          ids.length > 0 || isOrgOwnerRef.current
            ? ids
            : authData?.userId
              ? [Number(authData.userId)]
              : [];
        if (!cancelled) {
          aggregateMemberIdsRef.current = effectiveIds;
          const sorted = [...effectiveIds].sort((a, b) => a - b);
          setSubTenantMemberKey(`${subId}:${sorted.join(',')}`);
        }
      } catch {
        if (!cancelled) {
          aggregateMemberIdsRef.current = [];
          setSubTenantMemberKey(`${subId}:`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [listingSubAccountId, rbac?.currentOrgId, authData?.userId]);

  // Fetch member IDs across ALL org accounts so the owner can resolve campaigns
  // created by any org member even when no sub-account scope is active (e.g. direct URL).
  // Root-account listAccountUsers often returns 0 rows because members are added to
  // sub-accounts only, so we sweep every account in the org.
  // ownerMemberIdsReady gates getCampaign on the details page until this is done.
  useEffect(() => {
    if (rbac?.isLoading) return;
    const isOwner = Boolean(rbac?.userContext?.is_org_owner);
    if (!isOwner) {
      ownerDefaultMemberIdsRef.current = null;
      setOwnerMemberIdsReady(true);
      return;
    }
    const orgId = rbac?.currentOrgId ?? null;
    if (!orgId) {
      const ownerExtId = ownerUserIdRef.current;
      ownerDefaultMemberIdsRef.current = ownerExtId != null ? [ownerExtId] : [];
      setOwnerMemberIdsReady(true);
      return;
    }
    setOwnerMemberIdsReady(false);
    let cancelled = false;
    (async () => {
      try {
        const allIds: number[] = [];
        const ownerExtId = ownerUserIdRef.current;
        if (ownerExtId != null && Number.isFinite(ownerExtId)) allIds.push(ownerExtId);
        const accounts = await listAllAccounts(orgId);
        await Promise.all(
          accounts.map(async (account) => {
            try {
              const ids = await fetchSubTenantMemberUserIds(account.id, { orgId, bypassCache: false });
              for (const id of ids) {
                if (!allIds.includes(id)) allIds.push(id);
              }
            } catch { /* skip individual account failures */ }
          })
        );
        if (!cancelled) {
          ownerDefaultMemberIdsRef.current = allIds;
          setOwnerMemberIdsReady(true);
        }
      } catch {
        if (!cancelled) {
          const ownerExtId = ownerUserIdRef.current;
          ownerDefaultMemberIdsRef.current = ownerExtId != null ? [ownerExtId] : [];
          setOwnerMemberIdsReady(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [rbac?.isLoading, rbac?.userContext?.is_org_owner, rbac?.currentOrgId]);

  const auth = authData || {};

  const client = useMemo(
    () =>
      getDigitalAdInstance(
        auth,
        setSessionExpiredOpen,
        aggregateMemberIdsRef,
        subTenantScopeActiveRef,
        listingSubAccountIdRef,
        ownerDefaultMemberIdsRef,
        isOrgOwnerRef,
        isRootContextActiveRef,
        ownerAccountIdRef
      ),
    [authData]
  );

  const handleSessionExpiredClose = () => {
    setSessionExpiredOpen(false);
    clearStorage();
    location.href = `${getSelfOriginForRedirects()}/v2/login`;
  };

  const effectiveMemberKey = isSubTenantListingScope ? subTenantMemberKey : '';
  // Ready when: no sub-tenant scope, or sub-tenant IDs have been fetched (key is non-empty and not loading).
  // Root org context also waits for the org-wide member sweep so sub-account campaigns are included.
  const memberIdsReady =
    !isSubTenantListingScope ||
    ((subTenantMemberKey !== '' && !subTenantMemberKey.startsWith('loading:')) &&
      (!isRootListing || ownerMemberIdsReady));
  // Include switched-member id so campaign lists refetch when owner switches into/out of a member.
  const digitalAdSubTenantScopeSignal = [
    effectiveMemberKey,
    String(listingSubAccountId ?? ''),
    String(selectedMemberExternalUserId ?? ''),
    String(rbac?.currentAccountId ?? ''),
    String(rbac?.defaultAccountId ?? ''),
    String(isRootListing ? '1' : '0'),
    isRootListing && ownerMemberIdsReady
      ? `org:${[...(ownerDefaultMemberIdsRef.current ?? [])].sort((a, b) => a - b).join(',')}`
      : '',
  ].join('|');

  return (
    <>
      <DigitalAdAxiosContext.Provider value={client}>
        <DigitalAdSubTenantMemberKeyContext.Provider value={digitalAdSubTenantScopeSignal}>
          <DigitalAdMemberIdsReadyContext.Provider value={memberIdsReady}>
            <DigitalAdOwnerMemberIdsReadyContext.Provider value={ownerMemberIdsReady}>
              {children}
            </DigitalAdOwnerMemberIdsReadyContext.Provider>
          </DigitalAdMemberIdsReadyContext.Provider>
        </DigitalAdSubTenantMemberKeyContext.Provider>
      </DigitalAdAxiosContext.Provider>

      {/* Session Expired Dialog */}
      <Dialog
        open={sessionExpiredOpen}
        onClose={handleSessionExpiredClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'warning.main' }} />
            <Typography variant="h6" component="div">
              Session Expired
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your session has expired. Please log in again to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSessionExpiredClose}
            variant="contained"
            color="primary"
            fullWidth
          >
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

